<template>
  <div class="shop-page">
    <!-- 1) Barra de anuncio -->
    <div class="announcement">Envíos a toda Colombia · Mínimo 6 unidades por pedido</div>

    <!-- 2) Header sticky -->
    <header class="header" :class="{ 'header--scrolled': scrolled }">
      <div class="container-x">
        <div class="header__inner">
          <div class="header__brand">
            <img
              v-if="logoSrc && !logoFailed"
              :src="logoSrc"
              alt="Logo"
              class="header__logo"
              @error="logoFailed = true"
            />
            <img v-else :src="logoFallback" alt="Logo" class="header__logo" />
            <span class="header__name">{{ nombreTienda }}</span>
          </div>

          <label class="header__search">
            <i class="fas fa-search header__search-icon"></i>
            <input
              v-model="search"
              type="text"
              class="header__search-input"
              placeholder="Buscar"
            />
            <button
              v-if="search"
              class="header__search-clear"
              aria-label="Limpiar búsqueda"
              @click="search = ''"
            >
              <i class="fas fa-times"></i>
            </button>
          </label>

          <button
            class="header__menu"
            aria-label="Ver categorías"
            @click="catMenuOpen = true"
          >
            <i class="fas fa-bars"></i>
          </button>
          <button class="header__cart" aria-label="Ver carrito" @click="openCart">
            <i class="fas fa-shopping-bag"></i>
            <span v-if="cartCount > 0" class="header__cart-bubble">{{ cartBubble }}</span>
          </button>
        </div>

        <div class="header__row2">
          <label class="header__search header__search--mobile">
            <i class="fas fa-search header__search-icon"></i>
            <input
              v-model="search"
              type="text"
              class="header__search-input"
              placeholder="Buscar"
            />
            <button
              v-if="search"
              class="header__search-clear"
              aria-label="Limpiar búsqueda"
              @click="search = ''"
            >
              <i class="fas fa-times"></i>
            </button>
          </label>
        </div>
      </div>
    </header>

    <!-- 3) Colección -->
    <main class="collection">
      <div class="container-x">
        <!-- Banners de promoción (imagen PC / celular, carrusel si hay más de uno) -->
        <section
          v-if="banners.length"
          class="banner"
          @mouseenter="stopBannerTimer"
          @mouseleave="startBannerTimer"
          @touchstart.passive="onBannerTouchStart"
          @touchend="onBannerTouchEnd"
        >
          <div class="banner__track">
            <picture
              v-for="(b, i) in banners"
              :key="'bn' + i"
              class="banner__slide"
              :class="{ 'banner__slide--active': i === bannerPos }"
            >
              <source v-if="b.mobile && b.desktop" media="(max-width: 749px)" :srcset="resolveImagen(b.mobile)" />
              <img :src="resolveImagen(b.desktop || b.mobile)" alt="Promoción" />
            </picture>
          </div>
          <template v-if="banners.length > 1">
            <button class="gal-arrow gal-arrow--prev" aria-label="Banner anterior" @click="bannerStep(-1)">
              <i class="fas fa-chevron-left"></i>
            </button>
            <button class="gal-arrow gal-arrow--next" aria-label="Banner siguiente" @click="bannerStep(1)">
              <i class="fas fa-chevron-right"></i>
            </button>
            <div class="pcard__dots">
              <button
                v-for="(b, i) in banners"
                :key="'bd' + i"
                class="pcard__dot"
                :class="{ 'pcard__dot--active': i === bannerPos }"
                :aria-label="'Banner ' + (i + 1)"
                @click="goBanner(i)"
              ></button>
            </div>
          </template>
        </section>

        <div class="collection__head">
          <h1>Catálogo Mayorista</h1>
          <p v-if="vendedorNombre">
            Atendido por <strong>{{ vendedorNombre }}</strong> · Pedidos por WhatsApp
          </p>
        </div>

        <!-- 4) Toolbar -->
        <div ref="toolbar" class="toolbar">
          <span class="toolbar__count">{{ productosFiltrados.length }} productos</span>
          <label class="toolbar__sort">
            <span class="toolbar__label">Ordenar por</span>
            <span class="select-wrap">
              <select v-model="sortBy">
                <option value="destacados">Destacados</option>
                <option value="az">Alfabéticamente A–Z</option>
                <option value="za">Z–A</option>
                <option value="precio_asc">Precio menor a mayor</option>
                <option value="precio_desc">Precio mayor a menor</option>
                <option value="stock">Mayor stock</option>
              </select>
              <i class="fas fa-chevron-down"></i>
            </span>
          </label>
        </div>

        <!-- 5) Chips de categorías (las flechas indican que hay más hacia ese lado) -->
        <div v-if="categorias.length" class="cat-chips-wrap">
          <button
            v-show="chipsCanLeft"
            class="cat-chips-arrow cat-chips-arrow--prev"
            aria-label="Ver categorías anteriores"
            @click="scrollChips(-1)"
          ><span><i class="fas fa-chevron-left"></i></span></button>
          <div ref="chips" class="cat-chips" @wheel="onChipsWheel" @scroll.passive="updateChipsArrows">
            <button
              class="cat-chip"
              :class="{ 'cat-chip--active': activeCat === null }"
              @click="activeCat = null"
            >
              Todas
            </button>
            <button
              v-for="cat in categorias"
              :key="cat.id"
              class="cat-chip"
              :class="{ 'cat-chip--active': activeCat === cat.id }"
              @click="activeCat = cat.id"
            >
              {{ cat.label }}
            </button>
          </div>
          <button
            v-show="chipsCanRight"
            class="cat-chips-arrow cat-chips-arrow--next"
            aria-label="Ver más categorías"
            @click="scrollChips(1)"
          ><span><i class="fas fa-chevron-right"></i></span></button>
        </div>

        <!-- 7) Estado de carga -->
        <div v-if="loading" class="pgrid">
          <div v-for="n in 8" :key="'sk-' + n" class="pcard skeleton">
            <div class="skeleton__media"></div>
            <div class="skeleton__line" style="width: 40%"></div>
            <div class="skeleton__line" style="width: 80%"></div>
            <div class="skeleton__line" style="width: 25%"></div>
          </div>
        </div>

        <!-- 7) Estado de error -->
        <div v-else-if="error" class="shop-state">
          <i class="fas fa-exclamation-circle shop-state__icon"></i>
          <p>Ocurrió un error al cargar el catálogo.</p>
          <button class="shop-state__link" @click="cargarCatalogo">Reintentar</button>
        </div>

        <!-- 7) Estado vacío -->
        <div v-else-if="productosFiltrados.length === 0" class="shop-state">
          <i class="fas fa-search shop-state__icon"></i>
          <p>No se encontraron productos</p>
          <button class="shop-state__link" @click="limpiarFiltros">Limpiar búsqueda</button>
        </div>

        <!-- 6) Grilla de productos: la tarjeta abre el modal con fotos y variantes -->
        <div v-else class="pgrid">
          <article
            v-for="p in productosFiltrados"
            :key="p.matid"
            class="pcard"
            :class="{ 'pcard--soldout': esAgotado(p) }"
            @click="abrirModal(p)"
          >
            <div class="pcard__media" @mousemove="onZoomMove" @mouseleave="onZoomLeave">
              <transition name="slidefade" mode="out-in">
                <img
                  v-if="fotoActual(p)"
                  :key="fotoActual(p)"
                  :src="fotoActual(p)"
                  :alt="p.descrip"
                  loading="lazy"
                  @error="onFotoError(p)"
                />
                <i v-else class="fas fa-tshirt pcard__placeholder"></i>
              </transition>

              <!-- Carrusel: flechas y puntos -->
              <template v-if="fotosVivas(p).length > 1">
                <button
                  class="pcard__arrow pcard__arrow--prev"
                  aria-label="Foto anterior"
                  @click.stop="pasarFoto(p, -1)"
                ><i class="fas fa-chevron-left"></i></button>
                <button
                  class="pcard__arrow pcard__arrow--next"
                  aria-label="Foto siguiente"
                  @click.stop="pasarFoto(p, 1)"
                ><i class="fas fa-chevron-right"></i></button>
                <div class="pcard__dots">
                  <button
                    v-for="(f, i) in fotosVivas(p)"
                    :key="f.i"
                    class="pcard__dot"
                    :class="{ 'pcard__dot--active': i === posFoto(p) }"
                    :aria-label="'Foto ' + (i + 1)"
                    @click.stop="p._slide = i"
                  ></button>
                </div>
              </template>

              <span v-if="esAgotado(p)" class="badge badge--soldout">Agotado</span>
              <span v-else-if="p.descuento > 0" class="badge badge--sale">-{{ formatoPct(p.descuento) }}%</span>
            </div>

            <div class="pcard__info">
              <h3 class="pcard__title">{{ p.descrip }}</h3>
              <div v-if="p.precio > 0" class="pcard__price-row">
                <span class="pcard__price">{{ formatoCOP(precioFinal(p)) }}</span>
                <span v-if="trm > 0" class="tag tag--cop">COP</span>
                <s v-if="p.descuento > 0" class="price-old">{{ formatoCOP(p.precio) }}</s>
                <span v-if="trm > 0" class="tag tag--usd">USD {{ formatoUSD(precioFinal(p)) }}</span>
              </div>
              <p v-else class="pcard__ref">Precio a consultar</p>
              <p v-if="p.codigo" class="pcard__ref">Ref. {{ p.codigo }}</p>
            </div>
          </article>
        </div>
      </div>
    </main>

    <!-- 8) Footer -->
    <footer class="shop-footer">
      <img v-if="logoSrc && !logoFailed" :src="logoSrc" alt="Logo" class="shop-footer__logo" @error="logoFailed = true" />
      <img v-else :src="logoFallback" alt="Logo" class="shop-footer__logo" />
      <p class="shop-footer__name">{{ nombreTienda }}</p>
      <p class="shop-footer__tagline">Catálogo mayorista · Mínimo 6 unidades por pedido</p>
      <p class="shop-footer__copy">© {{ anio }} {{ nombreTienda }}. Todos los derechos reservados.</p>
    </footer>

    <!-- Menú de categorías: cubre todo el catálogo -->
    <transition name="ofade">
      <div v-if="catMenuOpen" class="catmenu">
        <div class="catmenu__head">
          <span class="catmenu__title">Categorías</span>
          <button class="icon-btn" aria-label="Cerrar" @click="catMenuOpen = false">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="catmenu__body">
          <button
            class="catmenu__item"
            :class="{ 'catmenu__item--active': activeCat === null }"
            @click="seleccionarCategoria(null)"
          >
            <span>Todo el catálogo</span>
            <span class="catmenu__count">{{ productos.length }}</span>
          </button>
          <div v-for="g in categoriaGrupos" :key="g.nombre || '_'" class="catmenu__group">
            <div v-if="g.nombre" class="catmenu__group-title">{{ g.nombre }}</div>
            <button
              v-for="cat in g.items"
              :key="cat.id"
              class="catmenu__item"
              :class="{ 'catmenu__item--active': activeCat === cat.id }"
              @click="seleccionarCategoria(cat.id)"
            >
              <span>{{ cat.nombre }}</span>
              <span class="catmenu__count">{{ conteoCategoria(cat) }}</span>
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- 9) Drawer del carrito -->
    <transition name="ofade">
      <div v-if="cartOpen" class="cart-overlay" @click="closeCart"></div>
    </transition>
    <aside class="cart" :class="{ 'cart--open': cartOpen }" aria-label="Carrito">
      <div class="cart__head">
        <h2 class="cart__title">Tu carrito ({{ cartCount }})</h2>
        <button class="icon-btn" aria-label="Cerrar carrito" @click="closeCart">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div v-if="cart.length === 0" class="cart__empty">
        <i class="fas fa-shopping-bag cart__empty-icon"></i>
        <p>Tu carrito está vacío</p>
        <button class="btn btn--dark btn--block" @click="closeCart">Continuar comprando</button>
      </div>

      <div v-else class="cart__items">
        <div v-for="item in cart" :key="item.key" class="cart__item">
          <div class="cart__thumb">
            <img v-if="item.imagen" :src="item.imagen" :alt="item.nombre" />
            <i v-else class="fas fa-tshirt"></i>
          </div>
          <div class="cart__item-info">
            <p class="cart__item-name">{{ item.nombre }}</p>
            <span v-if="item.detalle" class="cart__item-variant">{{ item.detalle }}</span>
            <p class="cart__item-price">{{ formatoCOP(item.precio) }} c/u</p>
            <div class="cart__item-row">
              <span class="stepper">
                <button aria-label="Restar" @click="cambiarCantidad(item, -1)">
                  <i class="fas fa-minus"></i>
                </button>
                <span class="stepper__num">{{ item.qty }}</span>
                <button
                  aria-label="Sumar"
                  :disabled="item.maxQty > 0 && item.qty >= item.maxQty"
                  @click="cambiarCantidad(item, 1)"
                >
                  <i class="fas fa-plus"></i>
                </button>
              </span>
              <button class="cart__item-remove" @click="eliminarLinea(item)">Eliminar</button>
            </div>
          </div>
          <span class="cart__item-total">{{ formatoCOP(item.precio * item.qty) }}</span>
        </div>
      </div>

      <div v-if="cart.length > 0" class="cart__foot">
        <div class="cart__minbar">
          <div class="cart__minbar-rail">
            <div class="cart__minbar-fill" :style="{ width: minProgress * 100 + '%' }"></div>
          </div>
          <p class="cart__minbar-text" :class="{ 'cart__minbar-text--ok': cartCount >= MINIMO }">
            {{ cartCount }}/{{ MINIMO }} unidades · mínimo por pedido
          </p>
        </div>

        <div class="cart__subtotal">
          <span class="cart__subtotal-label">Subtotal</span>
          <span class="cart__subtotal-value">
            {{ formatoCOP(cartTotal) }}
            <span v-if="trm > 0" class="tag tag--cop">COP</span>
            <span v-if="trm > 0" class="tag tag--usd">USD {{ formatoUSD(cartTotal) }}</span>
          </span>
        </div>
        <div class="cart__shipping">
          <span class="cart__subtotal-label">Envío</span>
          <span class="cart__shipping-value">Se cotiza al despachar</span>
        </div>

        <p class="cart__note">
          <i class="fas fa-truck"></i> El valor del envío se cotiza al momento del despacho.
        </p>
        <p class="cart__note">
          <i class="fab fa-whatsapp"></i> El pedido se envía y confirma por WhatsApp.
        </p>

        <form class="cart__form" @submit.prevent="enviarPedido">
          <input
            v-model="form.nombre"
            type="text"
            class="field"
            placeholder="Nombre completo *"
          />
          <div class="cart__form-row">
            <select v-model="form.departamento" class="field">
              <option value="" disabled>Departamento *</option>
              <option v-for="d in dptos" :key="d" :value="d">{{ d }}</option>
            </select>
            <select v-model="form.ciudad" class="field" :disabled="!form.departamento">
              <option value="" disabled>Ciudad *</option>
              <option v-for="c in ciudadesFiltradas" :key="c" :value="c">{{ c }}</option>
            </select>
          </div>
          <input
            v-model="form.direccion"
            type="text"
            class="field"
            placeholder="Dirección *"
          />

          <button
            type="submit"
            class="btn btn--dark btn--block"
            :disabled="!formCompleta || sending"
          >
            <i v-if="sending" class="fas fa-spinner fa-spin"></i>
            <template v-else><i class="fab fa-whatsapp"></i> Enviar pedido por WhatsApp</template>
          </button>
          <p v-if="orderError" class="cart__error">{{ orderError }}</p>
        </form>
      </div>
    </aside>

    <!-- 10) Modal de producto: carrusel de fotos + variantes -->
    <transition name="vfade">
      <div v-if="modal.open" class="vmodal-overlay" @click.self="closeModal">
        <div class="vmodal" role="dialog" aria-modal="true">
          <div class="vmodal__head">
            <div class="vmodal__head-text">
              <h3 class="vmodal__title">{{ modal.producto ? modal.producto.descrip : '' }}</h3>
              <span v-if="modal.producto && modal.producto.codigo" class="vmodal__ref">
                Ref. {{ modal.producto.codigo }}
              </span>
            </div>
            <button class="icon-btn" aria-label="Cerrar" @click="closeModal">
              <i class="fas fa-times"></i>
            </button>
          </div>

          <div class="vmodal__body">
            <!-- Carrusel -->
            <div
              class="pgal"
              :class="{ 'pgal--zoom': modal.zoom }"
              @touchstart.passive="onGalTouchStart"
              @touchmove="onGalTouchMove"
              @touchend="onGalTouchEnd"
              @mousemove="onZoomMove"
              @mouseleave="onZoomLeave"
            >
              <transition name="slidefade" mode="out-in">
                <img
                  v-if="modalFotoUrl"
                  :key="modalFotoUrl"
                  :src="modalFotoUrl"
                  :alt="modal.producto ? modal.producto.descrip : ''"
                  @error="onModalFotoError"
                />
                <i v-else class="fas fa-tshirt pcard__placeholder"></i>
              </transition>
              <span v-if="modal.producto && modal.producto.existencia > 0" class="badge badge--stock">
                Stock: {{ stockLabel(modal.producto.existencia) }}
              </span>
              <button
                v-if="modalFotoUrl"
                class="pgal__zoom"
                :aria-label="modal.zoom ? 'Quitar zoom' : 'Ampliar imagen'"
                @click.stop="toggleModalZoom"
              >
                <i :class="modal.zoom ? 'fas fa-search-minus' : 'fas fa-search-plus'"></i>
              </button>
              <template v-if="modalFotos.length > 1">
                <button class="gal-arrow gal-arrow--prev" aria-label="Foto anterior" @click="modalStep(-1)">
                  <i class="fas fa-chevron-left"></i>
                </button>
                <button class="gal-arrow gal-arrow--next" aria-label="Foto siguiente" @click="modalStep(1)">
                  <i class="fas fa-chevron-right"></i>
                </button>
                <span class="pgal__counter">{{ modal.pos + 1 }} / {{ modalFotos.length }}</span>
              </template>
            </div>
            <div v-if="modalFotos.length > 1" class="pgal__thumbs">
              <button
                v-for="(f, i) in modalFotos"
                :key="f.i"
                class="pgal__thumb"
                :class="{ 'pgal__thumb--active': i === modal.pos }"
                :aria-label="'Foto ' + (i + 1)"
                @click="modal.pos = i"
              >
                <img :src="f.url" alt="" loading="lazy" />
              </button>
            </div>

            <div class="vmodal__section">
              <div v-if="modal.producto && modal.producto.precio > 0" class="vprice">
                <span class="vprice__label">Precio</span>
                <div class="vprice__row">
                  <span class="vprice__value">{{ formatoCOP(precioFinal(modal.producto)) }}</span>
                  <span class="vprice__cur">COP</span>
                  <s v-if="modal.producto.descuento > 0" class="vprice__old">{{ formatoCOP(modal.producto.precio) }}</s>
                  <span v-if="modal.producto.descuento > 0" class="tag tag--sale">
                    -{{ formatoPct(modal.producto.descuento) }}%
                  </span>
                </div>
                <span v-if="trm > 0" class="tag tag--usd vprice__usd">
                  USD {{ formatoUSD(precioFinal(modal.producto)) }}
                </span>
              </div>

              <p class="vmodal__hint">
                {{ modal.single ? 'Selecciona la cantidad.' : 'Selecciona las cantidades por talla y color.' }}
                Podrás ajustarlas en el carrito.
              </p>

              <div v-if="modal.loading" class="vmodal__loading">
                <i class="fas fa-spinner fa-spin"></i>
              </div>
              <div v-else class="vmodal__list">
                <p v-if="filasModal.length === 0" class="vmodal__vacio">
                  No hay tallas disponibles por ahora.
                </p>
                <div v-for="s in filasModal" :key="s.serialid" class="vrow">
                  <span class="vrow__name" :title="nombreVariante(s)">{{ nombreVariante(s) }}</span>
                  <span v-if="s.colormin" class="color-dot" :style="{ background: s.colormin }"></span>
                  <span class="vrow__disp">Disp.: {{ s.saldo }}</span>
                  <span class="stepper stepper--modal">
                    <button
                      aria-label="Restar"
                      :disabled="qtyDe(s.serialid) <= 0"
                      @click="fijarCantidad(s, qtyDe(s.serialid) - 1)"
                    >
                      <i class="fas fa-minus"></i>
                    </button>
                    <input
                      class="stepper__input"
                      type="text"
                      inputmode="numeric"
                      :value="qtyDe(s.serialid)"
                      @input="onQtyInput(s, $event)"
                    />
                    <button
                      aria-label="Sumar"
                      :disabled="qtyDe(s.serialid) >= s.saldo"
                      @click="fijarCantidad(s, qtyDe(s.serialid) + 1)"
                    >
                      <i class="fas fa-plus"></i>
                    </button>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="vmodal__foot">
            <span class="vmodal__total">Total: <strong>{{ modalTotal }}</strong></span>
            <button
              class="btn btn--dark"
              :disabled="modalTotal <= 0"
              @click="confirmarVariantes"
            >
              <i class="fas fa-cart-plus"></i> Agregar al carrito
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- 11) Botón flotante del carrito: aparece cuando hay productos -->
    <transition name="fab">
      <button
        v-if="cart.length > 0 && !cartOpen && !modal.open"
        class="cart-fab"
        :class="{ 'cart-fab--bump': fabBump }"
        aria-label="Ver carrito"
        @click="openCart"
      >
        <i class="fas fa-shopping-cart"></i>
        <span class="cart-fab__badge">{{ cartBubble }}</span>
      </button>
    </transition>
  </div>
</template>

<script>
import catalogService from '@/services/catalog.service';
import logoFallback from '@/assets/img/logo-sm.png';

// Mínimo de unidades totales por pedido
const MIN_TOTAL_UNIDADES = 6;

const modalVacio = () => ({
  open: false,
  loading: false,
  producto: null,
  serials: [],
  single: false,
  qty: {},
  // Carrusel del modal
  pos: 0,
  zoom: false,
});

export default {
  name: 'PublicCatalog',
  data() {
    return {
      MINIMO: MIN_TOTAL_UNIDADES,
      anio: new Date().getFullYear(),
      nombreTienda: '',
      token: '',
      logoFallback,
      logoSrc: '',
      logoFailed: false,
      vendedorNombre: '',
      trm: 0,
      // Banners de promoción: [{ desktop, mobile }] con rutas relativas al storage
      banners: [],
      bannerPos: 0,
      bannerTimer: null,
      bannerTouchX: null,
      productos: [],
      loading: true,
      error: false,
      search: '',
      sortBy: 'destacados',
      activeCat: null,
      catMenuOpen: false,
      chipsCanLeft: false,
      chipsCanRight: false,
      scrolled: false,
      // Carrito (agrupa por matid + nroserial)
      cart: [],
      cartOpen: false,
      fabBump: false,
      // Modal de producto (fotos + variantes)
      modal: modalVacio(),
      galTouchX: null,
      // Checkout
      form: { nombre: '', departamento: '', ciudad: '', direccion: '' },
      ciudadesData: [],
      sending: false,
      orderError: '',
    };
  },
  computed: {
    cartCount() {
      return this.cart.reduce((s, i) => s + i.qty, 0);
    },
    cartTotal() {
      return this.cart.reduce((s, i) => s + i.precio * i.qty, 0);
    },
    cartBubble() {
      return this.cartCount > 99 ? '99+' : String(this.cartCount);
    },
    minProgress() {
      return Math.min(this.cartCount / MIN_TOTAL_UNIDADES, 1);
    },
    formCompleta() {
      const f = this.form;
      return !!(f.nombre && f.departamento && f.ciudad && f.direccion) && !this.sending;
    },
    dptos() {
      return [...new Set(this.ciudadesData.map((c) => c.DPTO))].sort((a, b) =>
        a.localeCompare(b, 'es')
      );
    },
    ciudadesFiltradas() {
      return this.ciudadesData
        .filter((c) => c.DPTO === this.form.departamento)
        .map((c) => c.NOMBRE)
        .sort((a, b) => a.localeCompare(b, 'es'));
    },
    categorias() {
      // Chips jerárquicos: el padre agrupa sus subcategorías
      const mapa = new Map();
      for (const p of this.productos) {
        if (p.categoria_id != null && p.categoria_padre_id != null) {
          if (!mapa.has(p.categoria_padre_id)) {
            mapa.set(p.categoria_padre_id, {
              id: p.categoria_padre_id,
              label: p.categoria_padre_nombre,
              nombre: p.categoria_padre_nombre,
              padre: null,
              esPadre: true,
            });
          }
          if (!mapa.has(p.categoria_id)) {
            mapa.set(p.categoria_id, {
              id: p.categoria_id,
              label: `${p.categoria_padre_nombre} > ${p.categoria_nombre}`,
              nombre: p.categoria_nombre,
              padre: p.categoria_padre_nombre,
              esPadre: false,
            });
          }
        } else if (p.categoria_id != null && !mapa.has(p.categoria_id)) {
          mapa.set(p.categoria_id, {
            id: p.categoria_id,
            label: p.categoria_nombre,
            nombre: p.categoria_nombre,
            padre: null,
            esPadre: false,
          });
        }
      }
      return [...mapa.values()];
    },
    // Categorías agrupadas por su padre para el menú (las sin padre van primero)
    categoriaGrupos() {
      const grupos = new Map([[null, []]]);
      this.categorias.forEach((c) => {
        if (!grupos.has(c.padre)) grupos.set(c.padre, []);
        grupos.get(c.padre).push(c);
      });
      return [...grupos.entries()]
        .filter(([, items]) => items.length)
        .map(([nombre, items]) => ({ nombre, items }));
    },
    productosFiltrados() {
      const q = this.search.trim().toLowerCase();
      let lista = this.productos.filter((p) => {
        if (q) {
          const campos = `${p.descrip} ${p.codigo} ${p.marca_descrip}`.toLowerCase();
          if (!campos.includes(q)) return false;
        }
        if (this.activeCat != null) {
          const cat = this.categorias.find((c) => c.id === this.activeCat);
          if (!cat) return false;
          if (cat.esPadre) {
            if (p.categoria_id !== this.activeCat && p.categoria_padre_id !== this.activeCat) return false;
          } else if (p.categoria_id !== this.activeCat) {
            return false;
          }
        }
        return true;
      });

      const por = this.sortBy;
      if (por === 'az') lista = [...lista].sort((a, b) => a.descrip.localeCompare(b.descrip, 'es'));
      else if (por === 'za') lista = [...lista].sort((a, b) => b.descrip.localeCompare(a.descrip, 'es'));
      else if (por === 'precio_asc') lista = [...lista].sort((a, b) => this.precioFinal(a) - this.precioFinal(b));
      else if (por === 'precio_desc') lista = [...lista].sort((a, b) => this.precioFinal(b) - this.precioFinal(a));
      else if (por === 'stock') lista = [...lista].sort((a, b) => b.existencia - a.existencia);
      return lista;
    },
    modalTotal() {
      return Object.values(this.modal.qty).reduce((s, n) => s + (parseInt(n, 10) || 0), 0);
    },
    // Filas del modal: variantes con existencias o, sin variantes, una fila para el producto
    filasModal() {
      const p = this.modal.producto;
      if (!p) return [];
      if (this.modal.single) {
        return [{ serialid: '_single', single: true, saldo: parseInt(p.existencia, 10) || 0 }];
      }
      return this.modal.serials.filter((s) => (parseInt(s.saldo, 10) || 0) > 0);
    },
    modalFotos() {
      return this.modal.producto ? this.fotosVivas(this.modal.producto) : [];
    },
    modalFotoUrl() {
      return this.modalFotos[this.modal.pos]?.url || null;
    },
  },
  watch: {
    // La ciudad se deshabilita hasta elegir departamento
    'form.departamento'() {
      this.form.ciudad = '';
    },
    cartOpen() {
      this.bloquearScroll();
    },
    'modal.open'() {
      this.bloquearScroll();
    },
    catMenuOpen() {
      this.bloquearScroll();
    },
    // Al cambiar de foto se quita el zoom
    'modal.pos'() {
      this.modal.zoom = false;
    },
    categorias() {
      this.$nextTick(this.updateChipsArrows);
    },
  },
  mounted() {
    this.token = this.$route.params.token;
    document.title = 'Catálogo Mayorista';
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.updateChipsArrows, { passive: true });
    window.addEventListener('keydown', this.onKeydown);
    this.cargarCatalogo();
    this.cargarAjustes();
    this.cargarCiudades();
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.updateChipsArrows);
    window.removeEventListener('keydown', this.onKeydown);
    this.stopBannerTimer();
    document.body.style.overflow = '';
  },
  methods: {
    onScroll() {
      this.scrolled = window.scrollY > 1;
    },
    onKeydown(e) {
      if (e.key === 'Escape') {
        if (this.modal.open) this.closeModal();
        else if (this.cartOpen) this.closeCart();
        else if (this.catMenuOpen) this.catMenuOpen = false;
      }
    },
    bloquearScroll() {
      const bloqueado = this.cartOpen || this.modal.open || this.catMenuOpen;
      document.body.style.overflow = bloqueado ? 'hidden' : '';
    },
    async cargarCatalogo() {
      this.loading = true;
      this.error = false;
      try {
        const res = await catalogService.getCatalogo(this.token);
        this.vendedorNombre = res.data.vendedor?.nombre || '';
        // Foto principal (imagen) + fotos adicionales del inventario, en ese orden
        this.productos = (res.data.productos || []).map((p) => ({
          ...p,
          precio: parseFloat(p.precio) || 0,
          descuento: Math.min(100, Math.max(0, parseFloat(p.descuento) || 0)),
          _fotos: [p.imagen, ...(p.fotos || [])].filter(Boolean).map(this.resolveImagen),
          _slide: 0,
          _dead: [],
        }));
      } catch (e) {
        console.error('Error cargando catálogo:', e);
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    async cargarAjustes() {
      try {
        const res = await catalogService.getPublicSettings();
        const s = res.data || {};
        this.trm = parseFloat(s.trm_dolar) || 0;
        // La marca se toma de la configuración; "MiStock" solo si no hay nada definido
        this.nombreTienda = String(s.company_name || '').trim() || 'MiStock';
        document.title = `Catálogo Mayorista — ${this.nombreTienda}`;
        const ruta = s.logo_url || s.logo_path || '';
        this.logoSrc = this.resolveImagen(ruta);
        this.banners = s.banners || [];
        this.startBannerTimer();
      } catch (e) {
        console.error('Error cargando ajustes públicos:', e);
      }
    },
    async cargarCiudades() {
      try {
        const res = await fetch(`${process.env.BASE_URL}ciudades.json?v=${Date.now()}`);
        this.ciudadesData = await res.json();
      } catch (e) {
        console.error('Error cargando ciudades:', e);
      }
    },
    // Las imágenes se resuelven contra VUE_APP_API_IMAGE_URL + /storage/{path}
    resolveImagen(ruta) {
      if (!ruta) return '';
      if (ruta.startsWith('http')) return ruta;
      const base = (process.env.VUE_APP_API_IMAGE_URL || '').trim().replace(/\/?$/, '/');
      return base + String(ruta).replace(/^\/?storage\//, '').replace(/^\/+/, '');
    },
    stockLabel(n) {
      return n >= 1000 ? '999+' : String(n);
    },
    esAgotado(p) {
      return (parseFloat(p.existencia) || 0) <= 0;
    },
    formatoCOP(v) {
      return '$' + Math.round(parseFloat(v) || 0).toLocaleString('es-CO');
    },
    formatoUSD(v) {
      return '$' + ((parseFloat(v) || 0) / this.trm).toFixed(2);
    },
    formatoPct(n) {
      return String(Math.round(n * 100) / 100).replace('.', ',');
    },
    // Precio con el descuento del producto (o el general de la tienda) ya aplicado
    precioFinal(p) {
      const d = p.descuento || 0;
      return d > 0 ? Math.round(p.precio * (1 - d / 100)) : p.precio;
    },
    limpiarFiltros() {
      this.search = '';
      this.activeCat = null;
    },
    conteoCategoria(cat) {
      return this.productos.filter((p) =>
        cat.esPadre
          ? p.categoria_id === cat.id || p.categoria_padre_id === cat.id
          : p.categoria_id === cat.id
      ).length;
    },
    // Elige la categoría desde el menú y sube hasta la toolbar si se está más abajo
    seleccionarCategoria(id) {
      this.activeCat = id;
      this.catMenuOpen = false;
      this.$nextTick(() => {
        const el = this.$refs.toolbar;
        if (!el) return;
        const headerH = this.$el.querySelector('.header')?.offsetHeight || 0;
        const top = el.getBoundingClientRect().top + window.scrollY - headerH - 8;
        if (window.scrollY > top) window.scrollTo({ top, behavior: 'smooth' });
      });
    },
    // ===== Zoom al pasar el mouse: la imagen se amplía siguiendo el cursor =====
    onZoomMove(e) {
      const el = e.currentTarget;
      const r = el.getBoundingClientRect();
      el.style.setProperty('--zx', `${((e.clientX - r.left) / r.width) * 100}%`);
      el.style.setProperty('--zy', `${((e.clientY - r.top) / r.height) * 100}%`);
    },
    onZoomLeave(e) {
      e.currentTarget.style.setProperty('--zx', '50%');
      e.currentTarget.style.setProperty('--zy', '50%');
    },
    // ===== Banners =====
    bannerStep(dir) {
      const n = this.banners.length;
      if (n < 2) return;
      this.bannerPos = (this.bannerPos + dir + n) % n;
      this.startBannerTimer();
    },
    goBanner(i) {
      this.bannerPos = i;
      this.startBannerTimer();
    },
    startBannerTimer() {
      this.stopBannerTimer();
      if (this.banners.length < 2) return;
      this.bannerTimer = setInterval(() => {
        this.bannerPos = (this.bannerPos + 1) % this.banners.length;
      }, 5000);
    },
    stopBannerTimer() {
      if (this.bannerTimer) clearInterval(this.bannerTimer);
      this.bannerTimer = null;
    },
    onBannerTouchStart(e) {
      this.bannerTouchX = e.changedTouches[0].clientX;
    },
    onBannerTouchEnd(e) {
      if (this.bannerTouchX === null) return;
      const dx = e.changedTouches[0].clientX - this.bannerTouchX;
      this.bannerTouchX = null;
      if (Math.abs(dx) > 40) this.bannerStep(dx < 0 ? 1 : -1);
    },
    // ===== Chips de categorías =====
    // Rueda vertical del mouse -> scroll horizontal de los chips
    onChipsWheel(e) {
      const el = e.currentTarget;
      if (el.scrollWidth <= el.clientWidth || Math.abs(e.deltaX) >= Math.abs(e.deltaY)) return;
      el.scrollLeft += e.deltaY;
      e.preventDefault();
    },
    // Muestra las flechas solo hacia el lado donde quedan categorías por ver
    updateChipsArrows() {
      const el = this.$refs.chips;
      if (!el) return;
      this.chipsCanLeft = el.scrollLeft > 2;
      this.chipsCanRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
    },
    scrollChips(dir) {
      const el = this.$refs.chips;
      if (el) el.scrollBy({ left: dir * el.clientWidth * 0.7, behavior: 'smooth' });
    },
    // ===== Carrusel de fotos de la tarjeta =====
    // Fotos que cargan bien (las que fallan se descartan del carrusel)
    fotosVivas(p) {
      return (p._fotos || [])
        .map((url, i) => ({ url, i }))
        .filter((f) => !p._dead.includes(f.i));
    },
    posFoto(p) {
      return Math.max(0, Math.min(p._slide || 0, this.fotosVivas(p).length - 1));
    },
    fotoActual(p) {
      const vivas = this.fotosVivas(p);
      return vivas.length ? vivas[this.posFoto(p)].url : null;
    },
    pasarFoto(p, dir) {
      const n = this.fotosVivas(p).length;
      if (n < 2) return;
      p._slide = (this.posFoto(p) + dir + n) % n;
    },
    onFotoError(p) {
      const vivas = this.fotosVivas(p);
      if (!vivas.length) return;
      p._dead = [...p._dead, vivas[this.posFoto(p)].i];
      p._slide = this.posFoto(p);
    },
    // ===== Carrusel del modal =====
    modalStep(dir) {
      const n = this.modalFotos.length;
      if (n < 2) return;
      this.modal.pos = (this.modal.pos + dir + n) % n;
    },
    onModalFotoError() {
      const p = this.modal.producto;
      const f = this.modalFotos[this.modal.pos];
      if (!p || !f) return;
      p._dead = [...p._dead, f.i];
      this.modal.pos = Math.min(this.modal.pos, Math.max(0, this.modalFotos.length - 1));
    },
    // Zoom con la lupa (pensado para táctil, donde no hay hover)
    toggleModalZoom(e) {
      this.modal.zoom = !this.modal.zoom;
      const el = e.currentTarget.closest('.pgal');
      el.style.setProperty('--zx', '50%');
      el.style.setProperty('--zy', '50%');
    },
    onGalTouchStart(e) {
      this.galTouchX = e.changedTouches[0].clientX;
    },
    // Con zoom activo, arrastrar el dedo recorre la imagen en vez de cambiar de foto
    onGalTouchMove(e) {
      if (!this.modal.zoom) return;
      e.preventDefault();
      const t = e.touches[0];
      const el = e.currentTarget;
      const r = el.getBoundingClientRect();
      const clamp = (v) => Math.min(100, Math.max(0, v));
      el.style.setProperty('--zx', `${clamp(((t.clientX - r.left) / r.width) * 100)}%`);
      el.style.setProperty('--zy', `${clamp(((t.clientY - r.top) / r.height) * 100)}%`);
    },
    onGalTouchEnd(e) {
      if (this.modal.zoom) {
        this.galTouchX = null;
        return;
      }
      if (this.galTouchX === null) return;
      const dx = e.changedTouches[0].clientX - this.galTouchX;
      this.galTouchX = null;
      if (Math.abs(dx) > 40) this.modalStep(dx < 0 ? 1 : -1);
    },
    // ===== Modal de producto =====
    // Se abre en la foto que se estaba viendo; sin variantes queda una fila "Unidades"
    abrirModal(producto) {
      if (this.esAgotado(producto)) return;
      const conVariantes = producto.variantes_count > 0;
      this.modal = {
        ...modalVacio(),
        open: true,
        loading: conVariantes,
        producto,
        single: !conVariantes,
        pos: this.posFoto(producto),
      };
      if (!conVariantes) return;
      catalogService
        .getSerials(this.token, producto.matid)
        .then((res) => {
          if (this.modal.producto !== producto) return;
          this.modal.serials = res.data || [];
          this.modal.single = this.modal.serials.length === 0;
          this.modal.loading = false;
        })
        .catch((e) => {
          console.error('Error cargando serials:', e);
          if (this.modal.producto !== producto) return;
          this.modal.single = true;
          this.modal.loading = false;
        });
    },
    closeModal() {
      this.modal = modalVacio();
    },
    nombreVariante(s) {
      if (s.single) return 'Unidades';
      const talla = (s.destalla || '').trim();
      const color = (s.descolor || '').trim();
      if (talla && color) return `${talla} / ${color}`;
      return talla || color || 'Variante';
    },
    qtyDe(serialid) {
      return this.modal.qty[serialid] || 0;
    },
    fijarCantidad(serial, valor) {
      let n = parseInt(valor, 10);
      if (isNaN(n) || n < 0) n = 0;
      if (n > serial.saldo) n = serial.saldo;
      // Reactivo: reasignar el objeto para que Vue detecte claves nuevas
      this.modal.qty = { ...this.modal.qty, [serial.serialid]: n };
      return n;
    },
    onQtyInput(serial, e) {
      e.target.value = this.fijarCantidad(serial, String(e.target.value).replace(/\D/g, ''));
    },
    confirmarVariantes() {
      const producto = this.modal.producto;
      let total = 0;
      for (const s of this.filasModal) {
        const qty = this.qtyDe(s.serialid);
        if (qty > 0) {
          this.agregarDirecto(producto, s.single ? null : s, qty);
          total += qty;
        }
      }
      this.closeModal();
      if (total > 0) {
        this.openCart();
        this.fabBump = true;
        setTimeout(() => { this.fabBump = false; }, 600);
      }
    },
    // Inserta o acumula una línea agrupada por matid + nroserial
    agregarDirecto(producto, serial, qty) {
      const nroserial = serial ? String(serial.serialid) : '0';
      const key = `${producto.matid}_${nroserial}`;
      const maxQty = serial ? parseInt(serial.saldo, 10) || 0 : parseInt(producto.existencia, 10) || 0;
      const existente = this.cart.find((i) => i.key === key);
      if (existente) {
        existente.qty = maxQty > 0 ? Math.min(existente.qty + qty, maxQty) : existente.qty + qty;
      } else {
        this.cart.push({
          key,
          matid: producto.matid,
          serialid: serial ? serial.serialid : null,
          nroserial,
          nombre: producto.descrip,
          detalle: serial
            ? [serial.destalla, serial.descolor].filter(Boolean).join(' / ')
            : '',
          // Precio con descuento; el servidor lo recalcula al registrar el pedido
          precio: this.precioFinal(producto),
          imagen: this.fotoActual(producto),
          qty,
          maxQty,
        });
      }
    },
    openCart() {
      this.cartOpen = true;
    },
    closeCart() {
      this.cartOpen = false;
    },
    cambiarCantidad(item, delta) {
      const nueva = item.qty + delta;
      if (nueva <= 0) {
        this.eliminarLinea(item);
        return;
      }
      if (delta > 0 && item.maxQty > 0 && nueva > item.maxQty) return;
      item.qty = nueva;
    },
    eliminarLinea(item) {
      this.cart = this.cart.filter((i) => i.key !== item.key);
    },
    async enviarPedido() {
      this.orderError = '';
      if (this.cartCount < MIN_TOTAL_UNIDADES) {
        this.orderError = `El pedido mínimo es de ${MIN_TOTAL_UNIDADES} unidades (llevas ${this.cartCount}).`;
        return;
      }
      if (!this.formCompleta) return;
      this.sending = true;
      try {
        const res = await catalogService.sendPedido(this.token, {
          items: this.cart.map((i) => ({ matid: i.matid, serialid: i.serialid, qty: i.qty })),
          cliente: {
            nombre: this.form.nombre,
            departamento: this.form.departamento,
            ciudad: this.form.ciudad,
            direccion: this.form.direccion,
          },
        });
        window.open(res.data.whatsapp_url, '_blank');
      } catch (e) {
        this.orderError =
          e.response?.data?.message || 'No se pudo enviar el pedido. Intenta de nuevo.';
      } finally {
        this.sending = false;
      }
    },
  },
};
</script>

<style>
@import url("https://fonts.googleapis.com/css2?family=Assistant:wght@400;500;600;700&display=swap");
</style>

<style scoped>
/* ===== Tokens de diseño ===== */
.shop-page {
  --ink: #121212;
  --ink-75: rgba(18, 18, 18, 0.75);
  --ink-55: rgba(18, 18, 18, 0.55);
  --line: rgba(18, 18, 18, 0.1);
  --brand: #0f3429;
  --brand-dark: #0a241c;

  background: #fff;
  color: var(--ink);
  font-family: "Assistant", "Segoe UI", system-ui, sans-serif;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}
.container-x {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 16px;
  width: 100%;
}
@media (min-width: 768px) {
  .container-x {
    padding: 0 32px;
  }
}

/* ===== 1) Barra de anuncio ===== */
.announcement {
  background: var(--ink);
  color: #fff;
  text-align: center;
  padding: 8px 16px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

/* ===== 2) Header sticky ===== */
.header {
  position: sticky;
  top: 0;
  z-index: 100;
  background: #fff;
  border-bottom: 1px solid var(--line);
  transition: box-shadow 0.2s;
}
.header--scrolled {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
}
.header__inner {
  display: flex;
  align-items: center;
  gap: 16px;
  height: 64px;
}
.header__brand {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}
.header__logo {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid var(--line);
  padding: 2px;
  object-fit: cover;
  background: #fff;
}
.header__name {
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}
.header__search {
  position: relative;
  display: block;
  flex: 1;
  max-width: 340px;
  margin-left: auto;
}
.header__search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 13px;
  color: var(--ink-55);
  pointer-events: none;
}
.header__search-input {
  width: 100%;
  border: 1px solid rgba(18, 18, 18, 0.2);
  border-radius: 999px;
  padding: 9px 34px 9px 36px;
  font-size: 13.5px;
  font-family: inherit;
  color: var(--ink);
  background: #fff;
  outline: none;
  transition: border-color 0.15s;
}
.header__search-input:focus {
  border-color: var(--ink);
}
.header__search-input::placeholder {
  color: rgba(18, 18, 18, 0.45);
}
.header__search-clear {
  position: absolute;
  right: 5px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--ink-55);
  font-size: 11px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.header__search-clear:hover {
  background: rgba(18, 18, 18, 0.06);
  color: var(--ink);
}
.header__cart {
  position: relative;
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--ink);
  font-size: 19px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}
.header__cart:hover {
  background: rgba(18, 18, 18, 0.05);
}
.header__cart-bubble {
  position: absolute;
  top: 2px;
  right: 1px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border-radius: 999px;
  background: var(--brand);
  color: #fff;
  font-size: 10.5px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 2px #fff;
}
.header__row2 {
  display: none;
}
.header__menu {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border: none;
  border-radius: 10px;
  background: var(--ink);
  color: #fff;
  font-size: 1.05rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}
.header__menu:hover {
  background: #000;
}

/* ===== Menú de categorías (pantalla completa) ===== */
.catmenu {
  position: fixed;
  inset: 0;
  z-index: 210;
  background: #fff;
  display: flex;
  flex-direction: column;
}
.catmenu__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--line);
}
.catmenu__title {
  font-size: 17px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}
.catmenu__body {
  flex: 1;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 8px 0 24px;
  width: 100%;
  max-width: 640px;
  margin: 0 auto;
}
.catmenu__group-title {
  font-size: 11.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--ink-55);
  padding: 18px 20px 6px;
}
.catmenu__item {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border: none;
  border-bottom: 1px solid var(--line);
  background: transparent;
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  color: var(--ink);
  text-align: left;
  padding: 14px 20px;
  cursor: pointer;
}
.catmenu__item:hover {
  background: rgba(18, 18, 18, 0.04);
}
.catmenu__item--active,
.catmenu__item--active:hover {
  background: var(--brand);
  color: #fff;
}
.catmenu__count {
  font-size: 12px;
  font-weight: 600;
  color: var(--ink-55);
  flex-shrink: 0;
}
.catmenu__item--active .catmenu__count {
  color: rgba(255, 255, 255, 0.75);
}

/* ===== Banners de promoción ===== */
.banner {
  position: relative;
  margin-bottom: 28px;
  border-radius: 12px;
  overflow: hidden;
  background: #f6f6f6;
  touch-action: pan-y;
}
/* Las diapositivas se apilan en la misma celda: el alto lo da la imagen más alta */
.banner__track {
  display: grid;
}
.banner__slide {
  grid-area: 1 / 1;
  align-self: center;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.5s;
}
.banner__slide--active {
  opacity: 1;
  pointer-events: auto;
}
.banner__slide img {
  width: 100%;
  height: auto;
  display: block;
}

/* ===== 3) Título de colección ===== */
.collection {
  padding-top: 36px;
  flex: 1;
}
.collection__head {
  text-align: center;
  margin-bottom: 24px;
}
.collection__head h1 {
  margin: 0;
  font-size: 28px;
  font-weight: 600;
  color: var(--ink);
}
.collection__head p {
  margin: 6px 0 0;
  font-size: 13.5px;
  color: var(--ink-55);
}
.collection__head p strong {
  font-weight: 600;
  color: var(--ink);
}

/* ===== 4) Toolbar ===== */
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 20px;
}
.toolbar__count {
  font-size: 13px;
  color: var(--ink-55);
}
.toolbar__sort {
  display: flex;
  align-items: center;
  gap: 8px;
}
.toolbar__label {
  font-size: 12.5px;
  color: var(--ink-55);
}
.select-wrap {
  position: relative;
  display: inline-flex;
}
.select-wrap select {
  appearance: none;
  -webkit-appearance: none;
  border: 1px solid rgba(18, 18, 18, 0.2);
  border-radius: 8px;
  background: #fff;
  padding: 8px 30px 8px 12px;
  font-size: 12.5px;
  font-weight: 600;
  font-family: inherit;
  color: var(--ink);
  cursor: pointer;
  outline: none;
}
.select-wrap i {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 10px;
  color: var(--ink-55);
  pointer-events: none;
}

/* ===== 5) Chips de categorías ===== */
.cat-chips-wrap {
  position: relative;
  margin: 0 -16px 20px;
}
.cat-chips {
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  padding: 0 16px 2px;
}
.cat-chips::-webkit-scrollbar {
  display: none;
}
@media (min-width: 768px) {
  .cat-chips-wrap {
    margin-left: -32px;
    margin-right: -32px;
  }
  .cat-chips {
    padding-left: 32px;
    padding-right: 32px;
  }
}
/* Flecha con degradado: indica que hay más categorías hacia ese lado */
.cat-chips-arrow {
  position: absolute;
  top: 0;
  bottom: 2px;
  z-index: 2;
  width: 56px;
  border: none;
  padding: 0;
  display: flex;
  align-items: center;
  cursor: pointer;
}
.cat-chips-arrow--prev {
  left: 0;
  justify-content: flex-start;
  padding-left: 8px;
  background: linear-gradient(to right, #fff 45%, rgba(255, 255, 255, 0));
}
.cat-chips-arrow--next {
  right: 0;
  justify-content: flex-end;
  padding-right: 8px;
  background: linear-gradient(to left, #fff 45%, rgba(255, 255, 255, 0));
}
.cat-chips-arrow span {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid rgba(18, 18, 18, 0.2);
  background: #fff;
  color: var(--ink);
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  transition: background 0.15s, color 0.15s;
}
.cat-chips-arrow:hover span {
  background: var(--ink);
  color: #fff;
}
.cat-chip {
  flex-shrink: 0;
  white-space: nowrap;
  border: 1px solid rgba(18, 18, 18, 0.2);
  background: #fff;
  color: var(--ink);
  border-radius: 999px;
  padding: 7px 16px;
  font-size: 12.5px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
}
.cat-chip:hover {
  border-color: var(--ink);
}
.cat-chip--active {
  background: var(--ink);
  border-color: var(--ink);
  color: #fff;
}

/* ===== 6) Grilla y tarjeta de producto ===== */
.pgrid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 28px 12px;
}
.pcard {
  display: flex;
  flex-direction: column;
  cursor: pointer;
}
.pcard--soldout {
  cursor: default;
}
.pcard__media {
  position: relative;
  aspect-ratio: 3 / 4;
  border-radius: 12px;
  overflow: hidden;
  background: #f6f6f6;
  transition: box-shadow 0.25s;
}
.pcard:hover .pcard__media {
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.08);
}
.pcard__media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transform-origin: var(--zx, 50%) var(--zy, 50%);
  transition: transform 0.35s;
}
/* Zoom al pasar el mouse (solo en dispositivos con puntero) */
@media (hover: hover) and (pointer: fine) {
  .pcard__media {
    cursor: zoom-in;
  }
  .pcard:hover .pcard__media img {
    transform: scale(1.6);
  }
}
.slidefade-enter-active,
.slidefade-leave-active {
  transition: opacity 0.25s;
}
.slidefade-enter-from,
.slidefade-leave-to {
  opacity: 0;
}
.pcard__placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 38px;
  color: rgba(18, 18, 18, 0.15);
}
.badge {
  position: absolute;
  top: 10px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 5px 10px;
  z-index: 1;
}
.badge--soldout {
  left: 10px;
  background: #fff;
  color: var(--ink);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}
.badge--sale {
  left: 10px;
  background: var(--brand);
  color: #fff;
}
.badge--stock {
  right: 10px;
  background: rgba(18, 18, 18, 0.72);
  color: #fff;
  -webkit-backdrop-filter: blur(2px);
  backdrop-filter: blur(2px);
}
.pcard__info {
  padding: 12px 2px 0;
  text-align: left;
}
.pcard__title {
  margin: 0;
  font-size: 13.5px;
  font-weight: 500;
  line-height: 1.45;
  color: var(--ink);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.pcard__price-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
}
.pcard__price {
  font-size: 14.5px;
  font-weight: 700;
}
.tag {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  border: 1px solid;
  padding: 1px 8px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.5;
}
.tag--cop {
  background: #eeeef4;
  color: #5c5c72;
  border-color: #d6d6e0;
}
.tag--usd {
  background: #e8f5ec;
  color: #1a7a3c;
  border-color: #bfe3cb;
}
.pcard__ref {
  margin: 2px 0 0;
  font-size: 11.5px;
  color: var(--ink-55);
}
.price-old {
  font-size: 11.5px;
  color: var(--ink-55);
}
.tag--sale {
  background: #e7efec;
  color: var(--brand);
  border-color: #b9cfc7;
}

/* Carrusel de la tarjeta (flechas y puntos) */
.pcard__arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 3;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid var(--line);
  color: var(--ink);
  font-size: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s, background 0.2s, color 0.2s;
}
.pcard__arrow--prev {
  left: 8px;
}
.pcard__arrow--next {
  right: 8px;
}
.pcard:hover .pcard__arrow {
  opacity: 1;
}
.pcard__arrow:hover {
  background: var(--ink);
  color: #fff;
}
@media (hover: none) {
  .pcard__arrow {
    opacity: 1;
  }
}
.pcard__dots {
  position: absolute;
  bottom: 10px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 5px;
  z-index: 3;
}
.pcard__dot {
  width: 6px;
  height: 6px;
  border-radius: 999px;
  border: none;
  padding: 0;
  background: rgba(255, 255, 255, 0.85);
  box-shadow: 0 1px 3px rgba(18, 18, 18, 0.3);
  cursor: pointer;
  transition: background 0.2s, width 0.2s;
}
.pcard__dot--active {
  background: var(--ink);
  width: 14px;
}

/* Flechas grandes (banner y carrusel del modal) */
.gal-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  width: 36px;
  height: 36px;
  border: 1px solid var(--line);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.92);
  color: var(--ink);
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, color 0.2s;
}
.gal-arrow--prev {
  left: 10px;
}
.gal-arrow--next {
  right: 10px;
}
.gal-arrow:hover {
  background: var(--ink);
  color: #fff;
}

/* ===== 7) Estados ===== */
.skeleton {
  pointer-events: none;
}
.skeleton__media {
  width: 100%;
  aspect-ratio: 3 / 4;
  border-radius: 12px;
  background: linear-gradient(100deg, #f0f0f0 40%, #f7f7f7 50%, #f0f0f0 60%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite linear;
}
.skeleton__line {
  height: 11px;
  border-radius: 6px;
  margin-top: 12px;
  background: linear-gradient(100deg, #f0f0f0 40%, #f7f7f7 50%, #f0f0f0 60%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite linear;
}
@keyframes shimmer {
  0% {
    background-position: 120% 0;
  }
  100% {
    background-position: -80% 0;
  }
}
.shop-state {
  text-align: center;
  padding: 72px 20px;
  color: var(--ink-55);
}
.shop-state__icon {
  font-size: 34px;
  opacity: 0.5;
}
.shop-state p {
  margin: 12px 0 0;
  font-size: 13.5px;
  color: var(--ink-75);
}
.shop-state__link {
  border: none;
  background: none;
  padding: 0;
  margin-top: 8px;
  font-size: 13px;
  font-family: inherit;
  color: var(--ink);
  text-decoration: underline;
  cursor: pointer;
}
.shop-state__link:hover {
  color: var(--brand-dark);
}

/* ===== 8) Footer ===== */
.shop-footer {
  border-top: 1px solid var(--line);
  margin-top: 48px;
  padding: 40px 20px 48px;
  text-align: center;
}
.shop-footer__logo {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1px solid var(--line);
  padding: 2px;
  object-fit: cover;
}
.shop-footer__name {
  margin: 12px 0 0;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}
.shop-footer__tagline {
  margin: 6px 0 0;
  font-size: 12.5px;
  color: var(--ink-55);
}
.shop-footer__copy {
  margin: 14px 0 0;
  font-size: 11.5px;
  color: rgba(18, 18, 18, 0.4);
}

/* ===== 9) Drawer del carrito ===== */
.cart-overlay {
  position: fixed;
  inset: 0;
  background: rgba(18, 18, 18, 0.45);
  z-index: 200;
}
.ofade-enter-active,
.ofade-leave-active {
  transition: opacity 0.2s;
}
.ofade-enter-from,
.ofade-leave-to {
  opacity: 0;
}
.cart {
  position: fixed;
  right: 0;
  top: 0;
  width: 100%;
  max-width: 430px;
  height: 100vh;
  height: 100dvh;
  background: #fff;
  z-index: 201;
  display: flex;
  flex-direction: column;
  box-shadow: -8px 0 32px rgba(0, 0, 0, 0.14);
  transform: translateX(100%);
  transition: transform 0.28s;
}
.cart--open {
  transform: translateX(0);
}
.cart__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  flex-shrink: 0;
}
.cart__title {
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  color: var(--ink);
}
.icon-btn {
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--ink);
  font-size: 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}
.icon-btn:hover {
  background: rgba(18, 18, 18, 0.06);
}
.cart__empty {
  text-align: center;
  padding: 48px 20px;
}
.cart__empty-icon {
  font-size: 34px;
  opacity: 0.4;
}
.cart__empty p {
  margin: 12px 0 16px;
  font-size: 13.5px;
  color: var(--ink-55);
}
.cart__items {
  flex: 1;
  overflow-y: auto;
  padding: 4px 20px;
}
.cart__item {
  display: flex;
  gap: 12px;
  padding: 16px 0;
  border-bottom: 1px solid rgba(18, 18, 18, 0.06);
}
.cart__item:last-child {
  border-bottom: none;
}
.cart__thumb {
  width: 68px;
  height: 88px;
  flex-shrink: 0;
  border-radius: 10px;
  background: #f6f6f6;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(18, 18, 18, 0.15);
  font-size: 22px;
}
.cart__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.cart__item-info {
  flex: 1;
  min-width: 0;
}
.cart__item-name {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.4;
}
.cart__item-variant {
  display: inline-flex;
  margin-top: 4px;
  background: #f4f4f4;
  border-radius: 999px;
  padding: 3px 8px;
  font-size: 10.5px;
  font-weight: 600;
  color: var(--ink-75);
}
.cart__item-price {
  margin: 4px 0 0;
  font-size: 11.5px;
  color: var(--ink-55);
}
.cart__item-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
}
.cart__item-remove {
  border: none;
  background: none;
  padding: 0;
  font-size: 11.5px;
  font-family: inherit;
  color: var(--ink-55);
  text-decoration: underline;
  cursor: pointer;
}
.cart__item-remove:hover {
  color: #b3261e;
}
.cart__item-total {
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
}
.cart__foot {
  border-top: 1px solid var(--line);
  padding: 16px 20px 20px;
  max-height: 62vh;
  overflow-y: auto;
  flex-shrink: 0;
}
.cart__minbar-rail {
  height: 3px;
  border-radius: 999px;
  background: #eee;
  overflow: hidden;
}
.cart__minbar-fill {
  height: 100%;
  border-radius: 999px;
  background: var(--brand);
  transition: width 0.3s;
}
.cart__minbar-text {
  margin: 6px 0 0;
  font-size: 10.5px;
  color: var(--ink-55);
}
.cart__minbar-text--ok {
  color: #1a7f4b;
}
.cart__subtotal {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 14px;
}
.cart__subtotal-label {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--ink-55);
}
.cart__subtotal-value {
  font-size: 18px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 6px;
}
.cart__subtotal-value .tag {
  font-size: 9.5px;
}
.cart__shipping {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 6px;
}
.cart__shipping-value {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--ink-75);
}
.cart__note {
  margin: 8px 0 0;
  font-size: 11.5px;
  color: var(--ink-55);
}
.cart__note + .cart__note {
  margin-top: 4px;
}
.cart__note i {
  width: 16px;
  text-align: center;
  margin-right: 2px;
}
.cart__note + .cart__form {
  margin-top: 12px;
}
.cart__form {
  display: grid;
  gap: 8px;
}
.cart__form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.field {
  width: 100%;
  border: 1px solid rgba(18, 18, 18, 0.25);
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13px;
  font-family: inherit;
  color: var(--ink);
  background: #fff;
  outline: none;
  transition: border-color 0.15s;
}
.field:focus {
  border-color: var(--ink);
}
.field:disabled {
  background: #f6f6f6;
  color: var(--ink-55);
  cursor: not-allowed;
}
.cart__error {
  margin: 0;
  font-size: 12px;
  color: #b3261e;
}

/* Botones oscuros */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-family: inherit;
  transition: opacity 0.15s, background 0.15s;
}
.btn--dark {
  background: var(--ink);
  color: #fff;
  padding: 14px 22px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.btn--dark:hover:not(:disabled) {
  background: #000;
}
.btn--dark:disabled {
  background: #e5e5e5;
  color: #9a9a9a;
  cursor: not-allowed;
}
.btn--block {
  width: 100%;
}

/* ===== Stepper compartido ===== */
.stepper {
  display: inline-flex;
  align-items: center;
  border: 1px solid rgba(18, 18, 18, 0.2);
  border-radius: 8px;
  overflow: hidden;
}
.stepper button {
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: var(--ink);
  font-size: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}
.stepper button:hover:not(:disabled) {
  background: rgba(18, 18, 18, 0.06);
}
.stepper button:disabled {
  opacity: 0.35;
  cursor: default;
}
.stepper__num {
  min-width: 30px;
  text-align: center;
  font-size: 12.5px;
  font-weight: 700;
}

/* ===== 10) Modal de variantes ===== */
.vfade-enter-active,
.vfade-leave-active {
  transition: opacity 0.2s;
}
.vfade-enter-from,
.vfade-leave-to {
  opacity: 0;
}
.vmodal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(18, 18, 18, 0.5);
  z-index: 300;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.vmodal {
  background: #fff;
  border-radius: 16px;
  width: 100%;
  max-width: 560px;
  max-height: 92vh;
  max-height: 92dvh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
}
.vmodal__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px 12px 20px;
  border-bottom: 1px solid var(--line);
  flex-shrink: 0;
}
.vmodal__head-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.vmodal__title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;
  color: var(--ink);
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.vmodal__ref {
  font-size: 11.5px;
  color: var(--ink-55);
}
.vmodal__body {
  overflow-y: auto;
}
.vmodal__section {
  padding: 14px 20px 16px;
}
.vmodal__loading {
  text-align: center;
  padding: 24px 0;
  font-size: 20px;
  color: var(--ink-55);
}
.vmodal__hint {
  margin: 0 0 12px;
  font-size: 12.5px;
  color: var(--ink-55);
}
.vmodal__vacio {
  margin: 0;
  padding: 14px 12px;
  border: 1px dashed rgba(18, 18, 18, 0.15);
  border-radius: 12px;
  text-align: center;
  font-size: 13px;
  color: var(--ink-55);
}
.vmodal__list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Carrusel del modal */
.pgal {
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 5;
  max-height: 52vh;
  background: #f6f6f6;
  overflow: hidden;
  touch-action: pan-y;
}
.pgal img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  transform-origin: var(--zx, 50%) var(--zy, 50%);
  transition: transform 0.25s;
}
@media (hover: hover) and (pointer: fine) {
  .pgal {
    cursor: zoom-in;
  }
  .pgal:hover img {
    transform: scale(2.2);
  }
}
.pgal--zoom {
  touch-action: none;
}
.pgal--zoom img {
  transform: scale(2.2);
}
.pgal__zoom {
  position: absolute;
  bottom: 10px;
  left: 10px;
  z-index: 2;
  width: 36px;
  height: 36px;
  border: 1px solid var(--line);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.92);
  color: var(--ink);
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, color 0.2s;
}
.pgal__zoom:hover,
.pgal--zoom .pgal__zoom {
  background: var(--ink);
  color: #fff;
}
.pgal__counter {
  position: absolute;
  bottom: 10px;
  right: 10px;
  z-index: 2;
  background: rgba(18, 18, 18, 0.65);
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  border-radius: 999px;
  padding: 3px 9px;
}
.pgal__thumbs {
  display: flex;
  gap: 6px;
  overflow-x: auto;
  scrollbar-width: none;
  padding: 10px 20px 0;
}
.pgal__thumbs::-webkit-scrollbar {
  display: none;
}
.pgal__thumb {
  flex-shrink: 0;
  width: 48px;
  height: 60px;
  padding: 0;
  border: 2px solid transparent;
  border-radius: 8px;
  overflow: hidden;
  background: #f6f6f6;
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.15s, border-color 0.15s;
}
.pgal__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.pgal__thumb--active {
  border-color: var(--ink);
  opacity: 1;
}

/* Bloque de precio del modal */
.vprice {
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 12px 14px;
  margin-bottom: 12px;
  background: #fafafa;
}
.vprice__label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--ink-55);
}
.vprice__row {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 8px;
  margin-top: 2px;
}
.vprice__value {
  font-size: 26px;
  font-weight: 700;
  line-height: 1.15;
  color: var(--ink);
}
.vprice__cur {
  font-size: 13px;
  font-weight: 600;
  color: var(--ink-55);
}
.vprice__old {
  font-size: 13px;
  color: var(--ink-55);
}
.vprice__row .tag {
  align-self: center;
}
.vprice__usd {
  margin-top: 6px;
}

/* Filas de variantes: una sola línea, el nombre se recorta con "…" si no cabe */
.vrow {
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid rgba(18, 18, 18, 0.12);
  border-radius: 12px;
  padding: 12px 14px;
  transition: border-color 0.15s;
}
.vrow:hover {
  border-color: var(--ink);
}
.vrow__name {
  flex: 1;
  min-width: 0;
  font-size: 15px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.vrow__disp {
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  color: #1a7f4b;
}
.color-dot {
  width: 13px;
  height: 13px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 1px solid rgba(18, 18, 18, 0.2);
}
.vrow .stepper button {
  width: 34px;
  height: 34px;
}
.vrow .stepper__input {
  width: 42px;
  height: 34px;
  font-size: 15px;
}
/* Celular: se compacta para que la fila completa quepa sin saltos de línea */
@media (max-width: 480px) {
  .vmodal-overlay {
    padding: 10px;
  }
  .vmodal__section {
    padding-left: 14px;
    padding-right: 14px;
  }
  .vrow {
    gap: 8px;
    padding: 10px;
  }
  .vrow__name {
    font-size: 14px;
  }
  .vrow__disp {
    font-size: 12px;
  }
  .vrow .stepper button {
    width: 30px;
    height: 30px;
  }
  .vrow .stepper__input {
    width: 34px;
    height: 30px;
    font-size: 14px;
  }
}
.stepper--modal {
  margin-left: auto;
  flex-shrink: 0;
}
.stepper__input {
  width: 34px;
  height: 28px;
  border: none;
  border-left: 1px solid rgba(18, 18, 18, 0.2);
  border-right: 1px solid rgba(18, 18, 18, 0.2);
  text-align: center;
  font-size: 12.5px;
  font-weight: 700;
  font-family: inherit;
  color: var(--ink);
  outline: none;
  -moz-appearance: textfield;
}
.stepper__input::-webkit-outer-spin-button,
.stepper__input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.vmodal__foot {
  border-top: 1px solid var(--line);
  padding: 14px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-shrink: 0;
}
.vmodal__total {
  font-size: 13px;
  color: var(--ink-55);
}
.vmodal__total strong {
  font-size: 15px;
  color: var(--ink);
}
.vmodal__foot .btn {
  padding: 12px 18px;
  flex-shrink: 0;
}

/* ===== 11) Botón flotante del carrito ===== */
.cart-fab {
  position: fixed;
  right: 18px;
  bottom: calc(18px + env(safe-area-inset-bottom, 0px));
  z-index: 150;
  width: 58px;
  height: 58px;
  border: none;
  border-radius: 50%;
  background: var(--ink);
  color: #fff;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  transition: background 0.2s, transform 0.2s;
}
.cart-fab:hover {
  background: #000;
}
.cart-fab--bump {
  animation: fabBump 0.6s;
}
.cart-fab__badge {
  position: absolute;
  top: -3px;
  right: -3px;
  min-width: 22px;
  height: 22px;
  padding: 0 5px;
  border-radius: 999px;
  background: var(--brand);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 2px #fff;
}
@keyframes fabBump {
  0%,
  100% {
    transform: scale(1);
  }
  40% {
    transform: scale(1.15);
  }
}
.fab-enter-active,
.fab-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}
.fab-enter-from,
.fab-leave-to {
  opacity: 0;
  transform: scale(0.6);
}

/* ===== Responsive ===== */
@media (min-width: 750px) {
  .pgrid {
    grid-template-columns: repeat(3, 1fr);
    gap: 36px 16px;
  }
}
@media (max-width: 749px) {
  .header__inner {
    height: 58px;
  }
  .header__inner .header__search {
    display: none;
  }
  .header__cart,
  .header__menu {
    margin-left: auto;
  }
  .header__menu + .header__cart {
    margin-left: 0;
    margin-right: -8px;
  }
  .header__row2 {
    display: block;
    padding-bottom: 12px;
  }
  .header__row2 .header__search {
    max-width: none;
    margin-left: 0;
  }
  .collection {
    padding-top: 28px;
  }
  .collection__head h1 {
    font-size: 24px;
  }
  .toolbar {
    flex-wrap: wrap;
  }
}
@media (min-width: 1100px) {
  .pgrid {
    grid-template-columns: repeat(4, 1fr);
  }
}
</style>
